<template>
  <Modal :name="$constants.dynamicModals.RESTORE_PASSWORD_MODAL" :custom="true" @before-open="restoreEmail">
    <div :class="modalClass">
      <CloseIcon width="24px" height="24px" class="restore-password-modal__close" @click="$modal.hide()" />

      <template v-if="!submitted">
        <BravadoText tag="h4" class="restore-password-modal__title">Forgot password!</BravadoText>

        <BravadoText tag="p" variant="body" class="restore-password-modal__body">
          Input your email below
          <br />
          to get a password reset link
        </BravadoText>

        <form class="restore-password-modal__form" @submit.prevent="handleReset">
          <BravadoTextInputV3
            v-model.trim="form.email"
            :is-valid="isEmailValid"
            :hint="emailErrorMessage"
            name="email"
            placeholder="Enter your email"
            @blur="$v.form.email.$touch"
          >
            <template slot="icon">
              <LockIcon width="20" height="20" />
            </template>
          </BravadoTextInputV3>
          <div class="restore-password-modal__field">
            <BravadoButtonV3 :variant="'primary'" :disabled="loading" :loading="loading" full-width>
              Request reset link
            </BravadoButtonV3>
          </div>
        </form>
        <div class="restore-password-modal__hint">
          Back to
          <NuxtLink
            :to="{ query: { dynamic_modal: $constants.dynamicModals.SIGN_IN_MODAL } }"
            class="restore-password-modal__link"
          >
            Sign In
          </NuxtLink>
        </div>
      </template>

      <template v-else>
        <BravadoText tag="p" class="restore-password-modal__feedback">
          You will receive an email with instructions on how to reset your password in a few minutes.
        </BravadoText>

        <BravadoButtonV3 variant="secondary" size="md" class="restore-password-modal__cta" @click="$modal.hide()">
          Got it
        </BravadoButtonV3>
      </template>
    </div>
  </Modal>
</template>

<script>
import Vue from 'vue';
import { validationMixin } from 'vuelidate';
import { email, required } from 'vuelidate/lib/validators';
import Modal from '~/components/common/Modal/Modal';
import CloseIcon from '~/assets/svg/close.vue.svg';
import BravadoTextInputV3 from '~/components/common/BravadoTextInputV3/BravadoTextInputV3';
import LockIcon from '~/assets/svg/u_lock.vue.svg';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3';

export default Vue.extend({
  name: 'RestorePasswordModal',
  components: {
    Modal,
    CloseIcon,
    BravadoButtonV3,
    BravadoTextInputV3,
    LockIcon,
  },
  mixins: [validationMixin],
  data() {
    return {
      form: {
        email: '',
      },
      errors: {},
      isValid: true,
      attemptedSubmission: false,
      loading: false,
      submitted: false,
    };
  },
  validations: {
    form: {
      email: { required, email },
    },
  },
  computed: {
    modalClass() {
      return {
        'restore-password-modal': true,
        'restore-password-modal--failed': !this.isValid,
        'restore-password-modal--submitted': this.submitted,
      };
    },
    emailErrorMessage() {
      if (this.isEmailValid) return '';
      if (!this.$v.form.email.required) return 'Email is required';
      if (!this.$v.form.email.email) return 'Please provide a valid email';
      if (this.errors.email) return this.errors.email;

      return '';
    },
    isEmailValid() {
      return !(this.attemptedSubmission && this.$v.form.email.$invalid) && !this.errors.email;
    },
  },
  watch: {
    'form.email'() {
      this.errors = {};
    },
  },
  methods: {
    // Restore email from prev forms
    restoreEmail() {
      try {
        const email = sessionStorage.getItem('email');
        if (email) this.form.email = email;
      } catch (e) {
        this.$sentryUtils.error(e);
      }
    },

    async handleReset() {
      this.errors = {};

      if (this.$v.form.$invalid) {
        this.attemptedSubmission = true;
      } else {
        try {
          this.loading = true;
          await this.$axios.$post('/users/password.json', { user: { email: this.form.email } });
          this.submitted = true;
        } catch (e) {
          this.isValid = false;

          if (e.response?.data?.errors) {
            this.errors = e.response.data.errors;
          } else {
            this.$notice.error('Something went wrong. Please try again later.', e);
          }

          setTimeout(() => {
            this.isValid = true;
          }, 3000);
          this.$sentryUtils.error(e);
        } finally {
          this.loading = false;
        }
      }
    },
  },
});
</script>

<style scoped lang="scss">
@import '~/assets/styles/uikit/utils/transition.scss';

.restore-password-modal {
  padding: 24px;
  background: var(--theme-color-background);
  width: 100%;
  min-height: 100vh;
  position: relative;
  text-align: center;
  color: var(--theme-color-text);

  @include mq('sm') {
    min-height: auto;
    max-width: 348px;
    box-shadow: var(--theme-elevation-15);
    border-radius: 16px;
  }

  &--failed {
    @extend %shake-transition;
  }

  &--submitted {
    text-align: center;
    //padding: 62px 24px 57px;
  }

  &__close {
    position: absolute;
    top: 17px;
    right: 16px;
    cursor: pointer;
    z-index: 2;
    color: var(--universal-color-gray-50);
    transition: color 0.2s ease-in-out;

    &:hover {
      color: var(--universal-color-gray-70);
    }
  }

  &__title,
  &__body {
    margin: 16px auto;
  }

  &__body {
    opacity: 0.6;
  }

  &__title {
    @include body-semibold;

    font-size: 22px;
    letter-spacing: -0.5px;
    line-height: 1.27272727;

    &--submitted {
      text-align: center;
    }
  }

  &__feedback {
    @include body-semibold;

    margin: 16px auto;

    &--submitted {
      text-align: center;
    }
  }

  &__field {
    position: relative;
    margin-top: 12px;
  }

  &__form {
    margin-top: 20px;
    text-align: left;
  }

  &__hint {
    @include body;

    margin-top: 21px;
    text-align: center;
    color: var(--universal-color-gray-50);
  }

  &__link {
    font-size: inherit;
    font-weight: inherit;
    font-family: inherit;
    color: var(--universal-color-brand);
    text-decoration: none;
  }

  &__cta {
    margin-top: 16px;
    background-color: var(--theme-color-over-card-hover);
  }

  ::v-deep .bravado-text-input-v3__icon {
    color: var(--universal-color-gray-50);
  }
}
</style>
